import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";
import { TouchTarget } from "./Button";
import { Link } from "./Link";

const colors = {
	red: "bg-error/15 text-error group-data-hover:bg-error/25",
	orange: "bg-warning/15 text-warning group-data-hover:bg-warning/25",
	amber: "bg-warning/15 text-warning group-data-hover:bg-warning/25",
	yellow: "bg-warning/15 text-warning group-data-hover:bg-warning/25",
	lime: "bg-success/15 text-success group-data-hover:bg-success/25",
	green: "bg-success/15 text-success group-data-hover:bg-success/25",
	emerald: "bg-success/15 text-success group-data-hover:bg-success/25",
	teal: "bg-success/15 text-success group-data-hover:bg-success/25",
	cyan: "bg-info/15 text-info group-data-hover:bg-info/25",
	sky: "bg-info/15 text-info group-data-hover:bg-info/25",
	blue: "bg-info/15 text-info group-data-hover:bg-info/25",
	indigo: "bg-primary/15 text-primary group-data-hover:bg-primary/25",
	violet: "bg-primary/15 text-primary group-data-hover:bg-primary/25",
	purple: "bg-primary/15 text-primary group-data-hover:bg-primary/25",
	fuchsia: "bg-secondary/15 text-secondary group-data-hover:bg-secondary/25",
	pink: "bg-secondary/15 text-secondary group-data-hover:bg-secondary/25",
	rose: "bg-secondary/15 text-secondary group-data-hover:bg-secondary/25",
	zinc: "bg-base-content/10 text-base-content group-data-hover:bg-base-content/20",
};

export type BadgeProps = { color?: keyof typeof colors };

export function Badge({
	color = "zinc",
	className,
	...props
}: BadgeProps & React.ComponentPropsWithoutRef<"span">) {
	return (
		<span
			{...props}
			className={clsx(
				className,
				"inline-flex items-center gap-x-1.5 rounded-md px-1.5 py-0.5 text-sm/5 font-medium sm:text-xs/5 forced-colors:outline",
				colors[color],
			)}
		/>
	);
}

export const BadgeButton = forwardRef(function BadgeButton(
	{
		color = "zinc",
		className,
		children,
		...props
	}: BadgeProps & { className?: string; children: React.ReactNode } & (
			| ({ href?: never } & Omit<Headless.ButtonProps, "as" | "className">)
			| ({ href: string } & Omit<
					React.ComponentPropsWithoutRef<typeof Link>,
					"className"
			  >)
		),
	ref: React.ForwardedRef<HTMLElement>,
) {
	const classes = clsx(
		className,
		"group relative inline-flex rounded-md focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary",
	);

	return typeof props.href === "string" ? (
		<Link
			{...props}
			className={classes}
			ref={ref as React.ForwardedRef<HTMLAnchorElement>}
		>
			<TouchTarget>
				<Badge color={color}>{children}</Badge>
			</TouchTarget>
		</Link>
	) : (
		<Headless.Button {...props} className={classes} ref={ref}>
			<TouchTarget>
				<Badge color={color}>{children}</Badge>
			</TouchTarget>
		</Headless.Button>
	);
});
